import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Form, Input } from 'reactstrap';
import {
  travelInsuranceInitialValues,
  travelInsuranceValidationSchema,
} from './constants';

const TravelInsurance = forwardRef(({ data, onDataChange }, ref) => {
  const {
    control,
    setValue,
    watch,
    trigger,
    getValues,
    setFocus,
    formState: { errors },
  } = useForm({
    defaultValues: travelInsuranceInitialValues,
    resolver: yupResolver(travelInsuranceValidationSchema),
    mode: 'onChange',
    shouldUnregister: false,
  });

  // Populate form values when data is available
  useEffect(() => {
    if (!data) return;

    if (data.InsuranceQuoteCost != null) {
      const n = Number(data.InsuranceQuoteCost);
      setValue('travelInsuranceCost', Number.isFinite(n) && n !== 0 ? n : null);
    }

    if (data.InsuranceComments != null) {
      setValue('travelInsuranceComments', data.InsuranceComments);
    }
  }, [data, setValue]);

  // Watch for form value changes and pass to parent
  const watched = watch();
  useEffect(() => {
    onDataChange?.(watched);
  }, [watched, onDataChange]);

  // expose methods to parent
  useImperativeHandle(ref, () => ({
    triggerAll: () => trigger(),
    triggerFields: names => trigger(names),
    getValues: () => getValues(),
    hasErrors: () => Object.keys(errors || {}).length > 0,
    focusFirstError: () => {
      const first = Object.keys(errors || {})[0];
      if (first) setFocus(first);
    },
  }));

  return (
    <div className="ba-form d-flex flex-column gap-2">
      <div>
        <span className="fw-semibold">Travel Insurance</span>
      </div>
      <Form className="d-flex flex-row gap-2">
        <Col lg={3} className="external-filter-col">
          <span className="ba-form-label">
            Costs<span className="text-danger-tr">*</span>
          </span>
          <Controller
            control={control}
            name="travelInsuranceCost"
            render={({ field }) => (
              <Input
                className={`external-filter-input ${errors.travelInsuranceCost ? 'is-invalid' : ''}`}
                {...field}
                placeholder="0"
                type="number"
                onBlur={e => {
                  const val = e.target.value;
                  field.onChange(val === '' ? '' : parseFloat(val).toFixed(2));
                }}
              />
            )}
          />
          {errors.travelInsuranceCost && (
            <span className="text-danger-tr">
              {errors.travelInsuranceCost.message || 'Costs is required'}
            </span>
          )}
        </Col>
        <Col lg={9} className="external-filter-col">
          <span className="ba-form-label">Comment</span>
          <Controller
            control={control}
            name="travelInsuranceComments"
            render={({ field }) => (
              <Input
                className="external-filter-input"
                {...field}
                placeholder="Travel Insurance Comment"
                type="text"
              />
            )}
          />
        </Col>
      </Form>
    </div>
  );
});

export default TravelInsurance;
